import type { Query } from '@tanstack/react-query';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

export const queryPersistenceDbName = 'agentteams-react-query-cache';
export const queryPersistenceStoreName = 'queryClient';
export const queryPersistenceClientKey = 'persisted-client';
export const queryPersistenceMaxAgeMs = 24 * 60 * 60 * 1000;

const queryPersistenceSchemaVersion = '1';

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });

const openQueryCacheDb = (indexedDb: IDBFactory): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDb.open(queryPersistenceDbName, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(queryPersistenceStoreName)) {
        db.createObjectStore(queryPersistenceStoreName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open query cache database'));
  });

const withQueryCacheStore = async <T>(
  indexedDb: IDBFactory,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => Promise<T>,
): Promise<T> => {
  const db = await openQueryCacheDb(indexedDb);

  try {
    const transaction = db.transaction(queryPersistenceStoreName, mode);
    const store = transaction.objectStore(queryPersistenceStoreName);
    return await operation(store);
  } finally {
    db.close();
  }
};

const getBrowserIndexedDb = (): IDBFactory | null => {
  if (typeof window === 'undefined') return null;
  return window.indexedDB ?? null;
};

export const createIndexedDbQueryPersister = (
  indexedDb: IDBFactory | null = getBrowserIndexedDb(),
): Persister | null => {
  if (!indexedDb) return null;

  return {
    persistClient: async (client: PersistedClient) => {
      try {
        await withQueryCacheStore(indexedDb, 'readwrite', async (store) => {
          await requestToPromise(store.put(client, queryPersistenceClientKey));
        });
      } catch {
        // Persistence is a UX optimization; query fetching must keep working if IndexedDB is unavailable.
      }
    },
    restoreClient: async () => {
      try {
        return await withQueryCacheStore(indexedDb, 'readonly', (store) =>
          requestToPromise<PersistedClient | undefined>(store.get(queryPersistenceClientKey)),
        );
      } catch {
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        await withQueryCacheStore(indexedDb, 'readwrite', async (store) => {
          await requestToPromise(store.delete(queryPersistenceClientKey));
        });
      } catch {
        // Logout/session cleanup should not be blocked by an IndexedDB failure.
      }
    },
  };
};

export const clearPersistedQueryClient = async (): Promise<void> => {
  const persister = createIndexedDbQueryPersister();
  if (!persister) return;
  await persister.removeClient();
};

export const getQueryPersistenceBuildVersion = () =>
  process.env.NEXT_PUBLIC_AGENTTEAMS_BUILD_GIT_SHA ??
  process.env.NEXT_PUBLIC_BUILD_GIT_SHA ??
  process.env.NEXT_PUBLIC_GITHUB_SHA ??
  process.env.NEXT_PUBLIC_CF_PAGES_COMMIT_SHA ??
  process.env.NEXT_PUBLIC_APP_VERSION ??
  'static';

export const getQueryPersistenceBuster = (memberId: string | null | undefined) =>
  [
    'member',
    memberId ?? 'anonymous',
    'build',
    getQueryPersistenceBuildVersion(),
    'schema',
    queryPersistenceSchemaVersion,
  ].join(':');

const isHomeQueryKey = (queryKey: readonly unknown[]) => {
  const [, scope] = queryKey;
  return (
    scope === 'home' ||
    scope === 'feed' ||
    scope === 'actionRequired' ||
    scope === 'activityHeatmap' ||
    scope === 'recentRuns' ||
    scope === 'projects'
  );
};

export const isPersistableQueryKey = (queryKey: readonly unknown[]) => {
  const [root, second, third] = queryKey;

  if (root === 'me') return isHomeQueryKey(queryKey);
  if (root === 'teams') return queryKey.length === 1;
  if (root === 'daemons') return second === undefined || second === 'shared';
  if (root === 'agentConfigs') return queryKey.length === 2;
  if (root === 'projects') {
    return queryKey.length === 2 || third === 'shareable' || third === 'archived';
  }

  return false;
};

export const shouldPersistQuery = (query: Query) =>
  query.state.status === 'success' && query.state.data !== undefined && isPersistableQueryKey(query.queryKey);
